import React, { useState, useEffect } from 'react';
import type { CommitteeMember, CommitteeMemberType } from '../../api/committee';
import { createCommitteeMember, updateCommitteeMember } from '../../api/committee';
import { getAllProfiles, type UserProfile } from '../../api/profile';

const MEMBER_TYPES: CommitteeMemberType[] = ['Committee', 'Trustee', 'Imaamat', 'Admin', 'Staff'];

interface CommitteeMemberModalProps {
  show: boolean;
  onHide: () => void;
  onSaved: () => void;
  member: CommitteeMember | null;
}

const CommitteeMemberModal: React.FC<CommitteeMemberModalProps> = ({ show, onHide, onSaved, member }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [description, setDescription] = useState('');
  const [memberType, setMemberType] = useState<CommitteeMemberType>('Committee');
  const [displayOrder, setDisplayOrder] = useState(0);
  const [imageId, setImageId] = useState<string | null>(null);
  const [, setImageFileName] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = !!member;

  useEffect(() => {
    if (member) {
      setFirstName(member.firstName);
      setLastName(member.lastName);
      setDescription(member.description ?? '');
      setMemberType(member.memberType);
      setDisplayOrder(member.displayOrder);
      setImageId(member.imageId);
      setImageFileName(member.imageFileName ?? null);
      setUserId(member.userId);
    } else {
      setFirstName('');
      setLastName('');
      setDescription('');
      setMemberType('Committee');
      setDisplayOrder(0);
      setImageId(null);
      setImageFileName(null);
      setUserId(null);
    }
    setError(null);
  }, [member, show]);

  useEffect(() => {
    if (show) {
      getAllProfiles().then(setUsers).catch(() => setUsers([]));
    }
  }, [show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!firstName.trim()) { setError('First name is required'); return; }
    if (!lastName.trim()) { setError('Last name is required'); return; }

    setIsLoading(true);
    try {
      const payload = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        position: memberType,
        description: description.trim() || null,
        memberType,
        displayOrder,
        imageId,
        userId,
      };
      if (isEdit && member) {
        await updateCommitteeMember(member.id, payload);
      } else {
        await createCommitteeMember(payload);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{isEdit ? 'Edit Member' : 'Add Member'}</h5>
            <button type="button" className="btn-close" onClick={onHide} disabled={isLoading} aria-label="Close" />
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger py-2" role="alert">
                  {error}
                </div>
              )}
              <div className="mb-3">
                <label className="form-label fw-semibold">Title / Type</label>
                <select
                  className="form-select"
                  value={memberType}
                  onChange={(e) => setMemberType(e.target.value as CommitteeMemberType)}
                  aria-label="Title (Committee, Trustee, Imaamat, Admin or Staff)"
                >
                  {MEMBER_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <small className="text-muted">Committee, Trustee, Imaamat, Admin or Staff — this is the member’s title.</small>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">First name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Last name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional short description"
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Link to user account</label>
                <select
                  className="form-select"
                  value={userId ?? ''}
                  onChange={(e) => setUserId(e.target.value || null)}
                >
                  <option value="">— None —</option>
                  {users.map((u) => (
                    <option key={u.userId} value={u.userId}>
                      {u.name} ({u.email})
                    </option>
                  ))}
                </select>
                <small className="text-muted">Optional: link this entry to an existing user.</small>
              </div>
              <div className="mb-3">
                <label className="form-label">Display order</label>
                <input
                  type="number"
                  className="form-control"
                  value={displayOrder}
                  onChange={(e) => setDisplayOrder(parseInt(e.target.value, 10) || 0)}
                  min={0}
                  style={{ maxWidth: 120 }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onHide} disabled={isLoading}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    Saving...
                  </>
                ) : (
                  isEdit ? 'Save changes' : 'Add member'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CommitteeMemberModal;
